import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Typography,
  Stack,
} from '@mui/material';
import {
  CloudUpload,
  AutoAwesome,
  Download,
  Refresh,
} from '@mui/icons-material';

export default function KitchenPreviewer() {
  const [file, setFile] = useState<File | null>(null);
  const [sourcePreview, setSourcePreview] = useState<string | null>(null);
  const [resultDataUrl, setResultDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setError(null);
    setResultDataUrl(null);
    const reader = new FileReader();
    reader.onload = () => setSourcePreview(reader.result as string);
    reader.readAsDataURL(f);
  };

  const handleGenerate = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResultDataUrl(null);

    try {
      const form = new FormData();
      form.append('image', file);

      const resp = await fetch('/api/kitchen-previewer/generate', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: form,
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Generation failed');
      if (!data.image) throw new Error('Server returned no image');
      setResultDataUrl(data.image);
    } catch (err: any) {
      setError(err?.message || 'Failed to generate kitchen preview');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setSourcePreview(null);
    setResultDataUrl(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AutoAwesome color="primary" />
        <Typography variant="h3">Kitchen Previewer</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a hand-drawn kitchen sketch and the AI will produce a photorealistic preview of how it would look installed.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 2 }}>Your Sketch</Typography>

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFilePicked}
            />

            {sourcePreview ? (
              <Box
                component="img"
                src={sourcePreview}
                alt="Uploaded sketch"
                sx={{
                  width: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 2,
                }}
              />
            ) : (
              <Box
                onClick={() => inputRef.current?.click()}
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 6,
                  textAlign: 'center',
                  cursor: 'pointer',
                  mb: 2,
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <CloudUpload sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  Tap to upload or take a photo of a sketch
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  JPEG, PNG, WebP or HEIC — up to 20 MB
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => inputRef.current?.click()}
                disabled={loading}
              >
                {file ? 'Choose different' : 'Choose image'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesome />}
                onClick={handleGenerate}
                disabled={!file || loading}
              >
                {loading ? 'Generating…' : 'Preview Kitchen'}
              </Button>
              {file && (
                <Button
                  variant="text"
                  startIcon={<Refresh />}
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 2 }}>AI Preview</Typography>

            {loading && (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Generating your kitchen — this usually takes 10–20 seconds.
                </Typography>
              </Box>
            )}

            {!loading && !resultDataUrl && (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 6,
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2">
                  The AI-generated kitchen will appear here after you click <strong>Preview Kitchen</strong>.
                </Typography>
              </Box>
            )}

            {resultDataUrl && (
              <>
                <Box
                  component="img"
                  src={resultDataUrl}
                  alt="AI-generated kitchen"
                  sx={{
                    width: '100%',
                    maxHeight: 500,
                    objectFit: 'contain',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 2,
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Download />}
                  href={resultDataUrl}
                  download={`kitchen-preview-${Date.now()}.png`}
                >
                  Download
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
